import {
  createNewVehiculeCheckType,
  getMatchingVehiculeCheckTypeError,
  getAllVehiculeCheckTypes,
  getGroupVehiculeCheckType,
  getTotalActiveVehiculeCheckTypes,
  getVehiculeCheckTypeById,
  getVehiculeCheckTypeByVehiculeCheckId,
  // getVehiculeCheckTypesByGroupId,
  listAllVehiculeCheckTypes,
  updateManyVehiculeCheckTypes,
  updateVehiculeCheckType,
} from '@/api/vehicule-check-type/vehicule-check-type';
import { CheckpointTypeFilterType } from '@/entities/checkpoint';
import { TVehiculeCheckTypeSchema } from '@/schemas/vehicule-check-type/vehicule-check-type.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { ListAllVehiculeCheckTypeDTO } from '@/api/entities/vehicule-check-type';
import { useAuth } from './auth.hooks';

export const useCreateVehiculeCheckType = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TVehiculeCheckTypeSchema) =>
      createNewVehiculeCheckType(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeCheckTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeCheckTypes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckTypeError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllVehiculeCheckTypes'],
      });

      notify(t('inspectionTypeCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingVehiculeTypeError = (
  query?: TVehiculeCheckTypeSchema,
) => {
  return useQuery({
    queryKey: ['getMatchingVehiculeCheckTypeError', query],
    queryFn: () => getMatchingVehiculeCheckTypeError(query),
  });
};

export const useListAllVehiculeCheckTypes = (
  query?: ListAllVehiculeCheckTypeDTO,
) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllVehiculeCheckTypes', query],
    queryFn: () =>
      listAllVehiculeCheckTypes({
        ...query,
        userId: limitedRoleLoggedInUserId,
      }),
  });
};

export const useGetAllVehiculeCheckTypes = (
  filter: CheckpointTypeFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllVehiculeCheckTypes', filter, page, sortBy, sortDirection],
    queryFn: () =>
      getAllVehiculeCheckTypes(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveVehiculeCheckTypes = () => {
  return useQuery({
    queryKey: ['getTotalActiveVehiculeCheckTypes'],
    queryFn: () => getTotalActiveVehiculeCheckTypes(),
  });
};

export const useGetVehiculeCheckTypeById = (id: string) => {
  return useQuery({
    queryKey: ['getVehiculeCheckTypeById', id],
    queryFn: () => getVehiculeCheckTypeById(id),
    enabled: !!id,
  });
};

// export const useGetVehiculeCheckTypesByGroupId = (groupId: string) => {
//   return useQuery({
//     queryKey: ['getVehiculeCheckTypesByGroupId', groupId],
//     queryFn: () => getVehiculeCheckTypesByGroupId(groupId),
//     enabled: !!groupId,
//   });
// };

export const useGetGroupVehiculeCheckType = (groupId: string) => {
  return useQuery({
    queryKey: ['getGroupVehiculeCheckType', groupId],
    queryFn: () => getGroupVehiculeCheckType(groupId),
    enabled: !!groupId,
  });
};

export const useGetVehiculeCheckTypeByVehiculeCheckId = (
  vehiculeCheckId: string,
) => {
  return useQuery({
    queryKey: ['getVehiculeCheckTypeByVehiculeCheckId', vehiculeCheckId],
    queryFn: () => getVehiculeCheckTypeByVehiculeCheckId(vehiculeCheckId),
    enabled: !!vehiculeCheckId,
  });
};

export const useUpdateVehiculeCheckType = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehiculeCheckType: TVehiculeCheckTypeSchema) =>
      updateVehiculeCheckType(vehiculeCheckType),
    onSuccess: (_, variables) => {
      const { id, groupId } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeCheckTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeCheckTypes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckTypeError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getVehiculeCheckTypeById', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['getGroupVehiculeCheckType', groupId],
      });

      notify(t('inspectionTypeUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating vehicule check type:', err.error);
    },
  });
};

export const useUpdateManyVehiculeCheckTypes = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehiculeCheckTypes: TVehiculeCheckTypeSchema[]) =>
      updateManyVehiculeCheckTypes(vehiculeCheckTypes),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getVehiculeCheckTypeById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeCheckTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeCheckTypes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckTypeError'],
      });

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('inspectionTypesUpdatedSuccessfully')
            : t('inspectionTypeUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicule check types:', error);
    },
  });
};

export const useGetVehiculeCheckTypesSelectOptions = (
  query?: ListAllVehiculeCheckTypeDTO,
) => {
  const {
    data: allVehiculeCheckTypes,
    isLoading: isLoadingAllVehiculeCheckTypes,
  } = useListAllVehiculeCheckTypes(query);

  const getVehiculeCheckTypesSelectOptions = () => {
    if (
      !isLoadingAllVehiculeCheckTypes &&
      allVehiculeCheckTypes &&
      allVehiculeCheckTypes.data.length > 0
    ) {
      return allVehiculeCheckTypes.data.map(
        (vehiculeCheckType: TVehiculeCheckTypeSchema) => {
          const { id, name } = vehiculeCheckType;

          return {
            label: name,
            value: id,
          };
        },
      );
    }

    return undefined;
  };

  return {
    getVehiculeCheckTypesSelectOptions,
    isLoadingAllVehiculeCheckTypes,
  };
};
